import { iconNavDots, iconDoc2aVia } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useRef, useState } from 'react';

import type { AdhesionTableItem, AdhesionTableProps } from '../../../types/previdencia/table/adhesionTableType';
import Tooltip from '../../tooltip/Tooltip';
import TableMenuPortal from '../shared/TableMenuPortal';
import { TABLE_MENU_ITEM_BUTTON_CLASS } from '../shared/tableMenuUtils';

// Helper function to format date from yyyy-MM-dd to dd/MM/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const DownloadAdhesionTable: React.FC<AdhesionTableProps> = ({
  adhesions,
  onRowClick,
  emptyMessage = 'Nenhuma adesão encontrada',
  onDownload,
  isLoading = false,
}) => {
  const [openItemMenuId, setOpenItemMenuId] = useState<string | number | null>(null);
  const itemMenuRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());
  const itemMenuButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());
  const itemDownloadButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());
  const emptyStateRef = useRef<HTMLTableCellElement>(null);

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close item menus
      if (openItemMenuId !== null) {
        const target = event.target as Node;
        const itemMenuRef = itemMenuRefs.current.get(openItemMenuId);
        const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
        if (
          itemMenuRef
          && !itemMenuRef.contains(target)
          && !itemMenuButtonRef?.contains(target)
        ) {
          setOpenItemMenuId(null);
        }
      }
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (openItemMenuId !== null) {
          const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
          setOpenItemMenuId(null);
          itemMenuButtonRef?.focus();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape as unknown as EventListener);
    };
  }, [openItemMenuId]);

  useEffect(() => {
    if (adhesions.length === 0) {
      emptyStateRef.current?.focus();
    }
  }, [adhesions.length]);

  useEffect(() => {
    if (openItemMenuId !== null) {
      const itemDownloadButtonRef = itemDownloadButtonRefs.current.get(openItemMenuId);
      itemDownloadButtonRef?.focus();
    }
  }, [openItemMenuId]);

  const handleRowClick = (adhesion: AdhesionTableItem) => {
    onRowClick?.(adhesion);
  };

  const handleDownload = (adhesion: AdhesionTableItem, event: React.MouseEvent) => {
    event.stopPropagation();
    onDownload?.(adhesion);
    setOpenItemMenuId(null);
  };

  const handleDownloadKeyDown = (adhesion: AdhesionTableItem, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onDownload?.(adhesion);
      setOpenItemMenuId(null);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(adhesion.id);
      itemMenuButtonRef?.focus();
    }
  };

  const handleRowKeyDown = (adhesion: AdhesionTableItem, event: React.KeyboardEvent) => {
    if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onRowClick(adhesion);
    }
  };

  const handleItemMenuToggle = (adhesionId: string | number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenItemMenuId(openItemMenuId === adhesionId ? null : adhesionId);
  };

  const handleItemMenuKeyDown = (adhesionId: string | number, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(openItemMenuId === adhesionId ? null : adhesionId);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (openItemMenuId !== adhesionId) {
        setOpenItemMenuId(adhesionId);
      } else {
        const itemDownloadButtonRef = itemDownloadButtonRefs.current.get(adhesionId);
        itemDownloadButtonRef?.focus();
      }
    } else if (event.key === 'Escape' && openItemMenuId === adhesionId) {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(adhesionId);
      itemMenuButtonRef?.focus();
    }
  };

  return (
    <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
      <div>
        <table className="min-w-[800px] w-full table-fixed" aria-label="Tabela de adesões">
          <thead>
            <tr>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="ID da Adesão">
                  <span>ID da Adesão</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Período Inicial">
                  <span>Período Inicial</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Período Final">
                  <span>Período Final</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Data do Status">
                  <span>Data do Status</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Ações">
                  <span>Ações</span>
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50 text-text-color">
            {adhesions && adhesions.length > 0 ? (
              adhesions.map((adhesion: AdhesionTableItem) => {
                return (
                  <tr
                    key={adhesion.id}
                    className={`hover:bg-gray-50 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 transition-colors text-xs ${onRowClick ? 'cursor-pointer' : ''
                      }`}
                    onClick={() => handleRowClick(adhesion)}
                    onKeyDown={(e) => handleRowKeyDown(adhesion, e)}
                    aria-label={
                      onRowClick
                        ? `Abrir detalhes da adesão ${adhesion.adhesionId}, Período: ${formatDate(adhesion.periodStart)} a ${formatDate(adhesion.periodEnd)}`
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : undefined}
                  >
                    <td className="px-4 py-2 whitespace-nowrap font-semibold">{adhesion.adhesionId || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(adhesion.periodStart) || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(adhesion.periodEnd) || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(adhesion.statusDate) || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Tooltip label="Ações da adesão">
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) {
                              itemMenuButtonRefs.current.set(adhesion.id, el);
                            } else {
                              itemMenuButtonRefs.current.delete(adhesion.id);
                            }
                          }}
                          onClick={(e) => handleItemMenuToggle(adhesion.id, e)}
                          onKeyDown={(e) => handleItemMenuKeyDown(adhesion.id, e)}
                          className="hover:bg-gray-100 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 rounded transition-colors p-1"
                          aria-label={`Ações da adesão ${adhesion.adhesionId}`}
                          aria-expanded={openItemMenuId === adhesion.id}
                          aria-haspopup="menu"
                          aria-controls={`adhesion-menu-${adhesion.id}`}
                          aria-pressed={openItemMenuId === adhesion.id}
                        >
                          <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
                        </button>
                      </Tooltip>
                      <TableMenuPortal
                        isOpen={openItemMenuId === adhesion.id}
                        anchorElement={itemMenuButtonRefs.current.get(adhesion.id) ?? null}
                        menuRef={(el) => {
                          if (el) {
                            itemMenuRefs.current.set(adhesion.id, el);
                          } else {
                            itemMenuRefs.current.delete(adhesion.id);
                          }
                        }}
                        align="right"
                        id={`adhesion-menu-${adhesion.id}`}
                        className="w-40"
                        aria-label={`Menu de ações da adesão ${adhesion.adhesionId}`}
                      >
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) {
                              itemDownloadButtonRefs.current.set(adhesion.id, el);
                            } else {
                              itemDownloadButtonRefs.current.delete(adhesion.id);
                            }
                          }}
                          onClick={(e) => handleDownload(adhesion, e)}
                          onKeyDown={(e) => handleDownloadKeyDown(adhesion, e)}
                          role="menuitem"
                          disabled={isLoading}
                          className={`${TABLE_MENU_ITEM_BUTTON_CLASS} ${isLoading
                            ? 'text-gray-400 cursor-not-allowed opacity-50'
                            : ''
                            }`}
                        >
                          <div className="flex items-center gap-2">
                            <BsdsIcon icon={iconDoc2aVia} className="w-4 h-4" aria-hidden="true" />
                            <span>Download</span>
                          </div>
                        </button>
                      </TableMenuPortal>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  ref={emptyStateRef}
                  tabIndex={-1}
                  colSpan={5}
                  className="px-4 py-4 text-left sm:text-center text-text-color text-xs focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                  role="status"
                  aria-live="polite"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DownloadAdhesionTable;

